/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
    AccountRole,
    combineCodec,
    getStructDecoder,
    getStructEncoder,
    getU64Decoder,
    getU64Encoder,
    getU8Decoder,
    getU8Encoder,
    transformEncoder,
    type AccountMeta,
    type AccountSignerMeta,
    type Address,
    type FixedSizeCodec,
    type FixedSizeDecoder,
    type FixedSizeEncoder,
    type Instruction,
    type InstructionWithAccounts,
    type InstructionWithData,
    type ReadonlyAccount,
    type ReadonlySignerAccount,
    type ReadonlyUint8Array,
    type TransactionSigner,
    type WritableAccount,
} from '@solana/kit';
import { TOKEN_2022_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const PERMISSIONED_BURN_CHECKED_DISCRIMINATOR = 46;

export function getPermissionedBurnCheckedDiscriminatorBytes() {
    return getU8Encoder().encode(PERMISSIONED_BURN_CHECKED_DISCRIMINATOR);
}

export const PERMISSIONED_BURN_CHECKED_PERMISSIONED_BURN_DISCRIMINATOR = 2;

export function getPermissionedBurnCheckedPermissionedBurnDiscriminatorBytes() {
    return getU8Encoder().encode(PERMISSIONED_BURN_CHECKED_PERMISSIONED_BURN_DISCRIMINATOR);
}

export type PermissionedBurnCheckedInstruction<
    TProgram extends string = typeof TOKEN_2022_PROGRAM_ADDRESS,
    TAccountAccount extends string | AccountMeta<string> = string,
    TAccountMint extends string | AccountMeta<string> = string,
    TAccountPermissionedBurnAuthority extends string | AccountMeta<string> = string,
    TAccountAuthority extends string | AccountMeta<string> = string,
    TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
    InstructionWithData<ReadonlyUint8Array> &
    InstructionWithAccounts<
        [
            TAccountAccount extends string ? WritableAccount<TAccountAccount> : TAccountAccount,
            TAccountMint extends string ? WritableAccount<TAccountMint> : TAccountMint,
            TAccountPermissionedBurnAuthority extends string
                ? ReadonlySignerAccount<TAccountPermissionedBurnAuthority> &
                      AccountSignerMeta<TAccountPermissionedBurnAuthority>
                : TAccountPermissionedBurnAuthority,
            TAccountAuthority extends string ? ReadonlyAccount<TAccountAuthority> : TAccountAuthority,
            ...TRemainingAccounts,
        ]
    >;

export type PermissionedBurnCheckedInstructionData = {
    discriminator: number;
    permissionedBurnDiscriminator: number;
    /** The amount of tokens to burn. */
    amount: bigint;
    /** Expected number of base 10 digits to the right of the decimal place. */
    decimals: number;
};

export type PermissionedBurnCheckedInstructionDataArgs = {
    /** The amount of tokens to burn. */
    amount: number | bigint;
    /** Expected number of base 10 digits to the right of the decimal place. */
    decimals: number;
};

export function getPermissionedBurnCheckedInstructionDataEncoder(): FixedSizeEncoder<PermissionedBurnCheckedInstructionDataArgs> {
    return transformEncoder(
        getStructEncoder([
            ['discriminator', getU8Encoder()],
            ['permissionedBurnDiscriminator', getU8Encoder()],
            ['amount', getU64Encoder()],
            ['decimals', getU8Encoder()],
        ]),
        value => ({
            ...value,
            discriminator: PERMISSIONED_BURN_CHECKED_DISCRIMINATOR,
            permissionedBurnDiscriminator: PERMISSIONED_BURN_CHECKED_PERMISSIONED_BURN_DISCRIMINATOR,
        }),
    );
}

export function getPermissionedBurnCheckedInstructionDataDecoder(): FixedSizeDecoder<PermissionedBurnCheckedInstructionData> {
    return getStructDecoder([
        ['discriminator', getU8Decoder()],
        ['permissionedBurnDiscriminator', getU8Decoder()],
        ['amount', getU64Decoder()],
        ['decimals', getU8Decoder()],
    ]);
}

export function getPermissionedBurnCheckedInstructionDataCodec(): FixedSizeCodec<
    PermissionedBurnCheckedInstructionDataArgs,
    PermissionedBurnCheckedInstructionData
> {
    return combineCodec(
        getPermissionedBurnCheckedInstructionDataEncoder(),
        getPermissionedBurnCheckedInstructionDataDecoder(),
    );
}

export type PermissionedBurnCheckedInput<
    TAccountAccount extends string = string,
    TAccountMint extends string = string,
    TAccountPermissionedBurnAuthority extends string = string,
    TAccountAuthority extends string = string,
> = {
    /** The source account to burn from. */
    account: Address<TAccountAccount>;
    /** The token mint. */
    mint: Address<TAccountMint>;
    /** Authority configured on the mint that must sign any permissioned burn instruction. */
    permissionedBurnAuthority: TransactionSigner<TAccountPermissionedBurnAuthority>;
    /** The account's owner/delegate or its multisignature account. */
    authority: Address<TAccountAuthority> | TransactionSigner<TAccountAuthority>;
    amount: PermissionedBurnCheckedInstructionDataArgs['amount'];
    decimals: PermissionedBurnCheckedInstructionDataArgs['decimals'];
    multiSigners?: Array<TransactionSigner>;
};

export function getPermissionedBurnCheckedInstruction<
    TAccountAccount extends string,
    TAccountMint extends string,
    TAccountPermissionedBurnAuthority extends string,
    TAccountAuthority extends string,
    TProgramAddress extends Address = typeof TOKEN_2022_PROGRAM_ADDRESS,
>(
    input: PermissionedBurnCheckedInput<
        TAccountAccount,
        TAccountMint,
        TAccountPermissionedBurnAuthority,
        TAccountAuthority
    >,
    config?: { programAddress?: TProgramAddress },
): PermissionedBurnCheckedInstruction<
    TProgramAddress,
    TAccountAccount,
    TAccountMint,
    TAccountPermissionedBurnAuthority,
    (typeof input)['authority'] extends TransactionSigner<TAccountAuthority>
        ? ReadonlySignerAccount<TAccountAuthority> & AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority
> {
    // Program address.
    const programAddress = config?.programAddress ?? TOKEN_2022_PROGRAM_ADDRESS;

    // Original accounts.
    const originalAccounts = {
        account: { value: input.account ?? null, isWritable: true },
        mint: { value: input.mint ?? null, isWritable: true },
        permissionedBurnAuthority: { value: input.permissionedBurnAuthority ?? null, isWritable: false },
        authority: { value: input.authority ?? null, isWritable: false },
    };
    const accounts = originalAccounts as Record<keyof typeof originalAccounts, ResolvedAccount>;

    // Original args.
    const args = { ...input };

    // Remaining accounts.
    const remainingAccounts: AccountMeta[] = (args.multiSigners ?? []).map(signer => ({
        address: signer.address,
        role: AccountRole.READONLY_SIGNER,
        signer,
    }));

    const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
    return Object.freeze({
        accounts: [
            getAccountMeta(accounts.account),
            getAccountMeta(accounts.mint),
            getAccountMeta(accounts.permissionedBurnAuthority),
            getAccountMeta(accounts.authority),
            ...remainingAccounts,
        ],
        data: getPermissionedBurnCheckedInstructionDataEncoder().encode(
            args as PermissionedBurnCheckedInstructionDataArgs,
        ),
        programAddress,
    } as PermissionedBurnCheckedInstruction<
        TProgramAddress,
        TAccountAccount,
        TAccountMint,
        TAccountPermissionedBurnAuthority,
        (typeof input)['authority'] extends TransactionSigner<TAccountAuthority>
            ? ReadonlySignerAccount<TAccountAuthority> & AccountSignerMeta<TAccountAuthority>
            : TAccountAuthority
    >);
}

export type ParsedPermissionedBurnCheckedInstruction<
    TProgram extends string = typeof TOKEN_2022_PROGRAM_ADDRESS,
    TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
    programAddress: Address<TProgram>;
    accounts: {
        /** The source account to burn from. */
        account: TAccountMetas[0];
        /** The token mint. */
        mint: TAccountMetas[1];
        /** Authority configured on the mint that must sign any permissioned burn instruction. */
        permissionedBurnAuthority: TAccountMetas[2];
        /** The account's owner/delegate or its multisignature account. */
        authority: TAccountMetas[3];
    };
    data: PermissionedBurnCheckedInstructionData;
};

export function parsePermissionedBurnCheckedInstruction<
    TProgram extends string,
    TAccountMetas extends readonly AccountMeta[],
>(
    instruction: Instruction<TProgram> &
        InstructionWithAccounts<TAccountMetas> &
        InstructionWithData<ReadonlyUint8Array>,
): ParsedPermissionedBurnCheckedInstruction<TProgram, TAccountMetas> {
    if (instruction.accounts.length < 4) {
        // TODO: Coded error.
        throw new Error('Not enough accounts');
    }
    let accountIndex = 0;
    const getNextAccount = () => {
        const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
        accountIndex += 1;
        return accountMeta;
    };
    return {
        programAddress: instruction.programAddress,
        accounts: {
            account: getNextAccount(),
            mint: getNextAccount(),
            permissionedBurnAuthority: getNextAccount(),
            authority: getNextAccount(),
        },
        data: getPermissionedBurnCheckedInstructionDataDecoder().decode(instruction.data),
    };
}
